import React from 'react';
import { Link } from 'react-router-dom';
import { Trophy, Leaf, FileText, PlayCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { heroContent, bulletinContent } from '../data/translationsHome';
import { ROUTE_MAP } from '../data/routes';

const HeroSection = () => {
  const { locale, t } = useLanguage();
  const routes = ROUTE_MAP[locale];
  const hero = heroContent[locale] || {};
  const bulletin = bulletinContent[locale] || {};
  const bulletinQuickLinks = [
    {
      key: 'customerStories',
      label: t('Başarı Hikayeleri', 'Success Stories', 'Uğur Hekayələri'),
      href: routes.customerStories,
      icon: Trophy
    },
    {
      key: 'energyTransition',
      label: t('Enerji Dönüşümü', 'Energy Transition', 'Enerji Keçidi'),
      href: routes.energyTransition,
      icon: Leaf
    },
    {
      key: 'docs',
      label: t('Dokümantasyon', 'Documentation', 'Dokumentasiya'),
      href: routes.docs,
      icon: FileText
    },
    {
      key: 'videos',
      label: t('Videolar', 'Videos', 'Videolar'),
      href: routes.videos,
      icon: PlayCircle
    }
  ];
  const heroBackgroundAlt = t(
    'Akıllı vana çözümleri tanıtım görseli',
    'Smart valve solutions hero visual',
    'Ağıllı vana həlləri tanıtım vizualı'
  );

  return (
    <section className="relative min-h-[75vh] flex items-center">
      {/* Background */}
      <div className="absolute inset-0">
        {hero?.backgroundImage && (
          <picture>
            <source media="(max-width: 640px)" srcSet="/images/solutions/ind.webp" />
            <img
              src={hero.backgroundImage}
              alt={heroBackgroundAlt}
              className="absolute inset-0 w-full h-full object-cover"
              width={1920}
              height={1075}
              sizes="100vw"
              loading="eager"
              fetchPriority="high"
              decoding="async"
            />
          </picture>
        )}
        <div className="absolute inset-0 bg-gradient-to-br from-[#213A8F]/55 via-[#213A8F]/25 to-[#0f1d37]/50" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="max-w-3xl">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            {hero?.title}
          </h1>
          <p className="text-xl md:text-2xl text-white/90 mb-8 leading-relaxed">
            {hero?.subtitle}
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link
              to={routes.solutions}
              data-testid="hero-cta"
              className="interactive-lift inline-flex items-center justify-center px-8 py-4 bg-white text-[#213A8F] font-medium rounded-lg hover:bg-gray-100 transition-colors"
            >
              {hero?.cta || t('Çözümleri İnceleyin', 'Explore Solutions', 'Həlləri İncele')}
            </Link>
            <Link
              to={routes.energyTransition}
              data-testid="hero-energy-transition-cta"
              className="interactive-lift inline-flex items-center justify-center px-8 py-4 bg-white text-[#213A8F] font-medium rounded-lg hover:bg-gray-100 transition-colors"
            >
              {t('Enerji Dönüşümü', 'Energy Transition', 'Enerji Keçidi')}
            </Link>
          </div>
        </div>
      </div>

      {/* Bulletin banner */}
      <div className="absolute bottom-0 left-0 right-0 bg-[#1a3a52]/95">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-0">
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_430px] gap-0 items-stretch min-h-[76px]">
            <div className="flex flex-col md:flex-row md:items-center gap-3">
              <Link
                to={routes.contact}
                className="interactive-lift inline-flex items-center justify-center px-6 py-2 bg-[#213A8F] text-white font-semibold rounded hover:bg-[#1A2F75] transition-colors whitespace-nowrap"
              >
                {bulletin?.cta}
              </Link>
              <p className="text-white text-[13px] xl:text-[14px] leading-none whitespace-nowrap overflow-hidden text-ellipsis">
                {bulletin?.title}
              </p>
            </div>

            <div className="hidden lg:flex items-stretch justify-end gap-1.5 w-[430px] justify-self-end bg-[#213A8F]/92 px-3">
              {bulletinQuickLinks.map((item) => (
                <Link
                  key={item.key}
                  to={item.href}
                  className="group flex-1 flex flex-col items-center justify-center rounded-md px-1 py-2 text-white/95 hover:text-white hover:bg-white/10 transition-colors"
                >
                  <item.icon className="w-[15px] h-[15px] mb-1" strokeWidth={2.1} />
                  <span className="text-[11px] leading-none text-center font-semibold whitespace-nowrap">{item.label}</span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
